import { useQuery } from '@tanstack/react-query';
import { aiAssistantApi } from '../api';
import type { AICapabilityState } from '../types';

/**
 * Reusable AI capability detector for the generative (LLM) layer.
 *
 * The backend masks LLM failures as HTTP 200 with a graceful payload, so status
 * codes cannot be used. We probe `/api/ai/follow-up-draft`, which returns a
 * CLEAN signal: `draft` is a non-empty string when the provider works, and
 * `null` when the provider call fails. This is a real call to a real endpoint —
 * no fabricated content, no fragile string matching.
 *
 * Returns the canonical capability state consumed by every Layer 2 surface.
 */
export function useAICapability() {
  const query = useQuery({
    queryKey: ['ai', 'capability'],
    queryFn: async (): Promise<'available' | 'unavailable'> => {
      const data = await aiAssistantApi.followUpDraft({ leadName: 'Capability Probe' });
      return data && typeof data.draft === 'string' && data.draft.trim().length > 0
        ? 'available'
        : 'unavailable';
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const state: AICapabilityState = query.isLoading
    ? 'loading'
    : query.isError
      ? 'error'
      : (query.data ?? 'unavailable');

  return { state, refetch: query.refetch, isFetching: query.isFetching };
}
